import { formatCurrency } from '@tallo/design-system';
import { RenterRequirementsMinimumIncome } from '@tallo/property';

import {
  RenterAccessmentBadge,
  RenterAssessmentItem,
  RenterAssessmentParams,
} from '../../interfaces/renter-profile-assessment.interface';

export const incomeAssessmentBadges: {
  pass: RenterAccessmentBadge;
  low: RenterAccessmentBadge;
} = {
  pass: { title: 'Meets income requirement', badgeColor: 'green' },
  low: { title: 'Below income requirement', badgeColor: 'red' },
};

export function getIncomeAssessment(assessmentParams: RenterAssessmentParams): RenterAssessmentItem {
  const incomeValue = assessmentParams.screening?.monthlyIncome || assessmentParams.renter?.monthlyIncome || 'N/A';
  const minimumIncomeRequirement = assessmentParams.property?.renterRequirements?.minimumIncome;
  const rent = assessmentParams.property?.leaseConditions?.rent;
  const hasHousingVoucher = assessmentParams.renter?.hasHousingVoucher;

  // Format UI representation
  const uiRepresentation = formatIncomeForUI(incomeValue);

  // Calculate minimum income requirement
  const minRequiredIncome = calculateMinimumIncome(rent, minimumIncomeRequirement);

  let badge: RenterAccessmentBadge | null = null;

  // Only assess if we have all required data
  if (minRequiredIncome !== null && incomeValue !== 'N/A') {
    const parsedIncome = parseIncome(incomeValue);

    if (parsedIncome) {
      if (parsedIncome.length === 1) {
        // Single number
        if (parsedIncome[0] >= minRequiredIncome) {
          badge = incomeAssessmentBadges.pass;
        } else if (!hasHousingVoucher) {
          badge = incomeAssessmentBadges.low;
        }
      } else if (parsedIncome.length === 2) {
        // Range
        const [min, max] = parsedIncome;
        if (min >= minRequiredIncome) {
          badge = incomeAssessmentBadges.pass;
        } else if (max < minRequiredIncome && !hasHousingVoucher) {
          badge = incomeAssessmentBadges.low;
        }
        // Partial overlap (min < requirement <= max) - no badge
      }
    }
  }

  return {
    uiRepresentation,
    badge,
  };
}

function formatIncomeForUI(incomeValue: string | number | null | undefined): string {
  if (!incomeValue || incomeValue === 'N/A') {
    return 'N/A';
  }

  const incomeStr = incomeValue.toString().trim();

  // Handle "Around X" format
  const aroundMatch = incomeStr.match(/^around\s+(\d+)$/i);
  if (aroundMatch) {
    return `Around ${formatCurrency(parseInt(aroundMatch[1], 10))}`;
  }

  // Handle "X+" format
  const plusMatch = incomeStr.match(/^(\d+)\+$/);
  if (plusMatch) {
    return `${formatCurrency(parseInt(plusMatch[1], 10))}+`;
  }

  // Handle range format "X-Y"
  const rangeMatch = incomeStr.match(/^(\d+)\s*[-–]\s*(\d+)$/);
  if (rangeMatch) {
    const min = parseInt(rangeMatch[1], 10);
    const max = parseInt(rangeMatch[2], 10);
    return `${formatCurrency(min)}–${formatCurrency(max)}`;
  }

  // Handle single number
  const singleMatch = incomeStr.match(/^\d+$/);
  if (singleMatch) {
    return formatCurrency(parseInt(incomeStr, 10));
  }

  // Return original if no format matches
  return incomeStr;
}

function parseIncome(incomeValue: string | number | null | undefined): number[] | null {
  if (!incomeValue) {
    return null;
  }

  const incomeStr = incomeValue.toString().trim();

  // Handle "Around X" format - treat as single value
  const aroundMatch = incomeStr.match(/^around\s+(\d+)$/i);
  if (aroundMatch) {
    return [parseInt(aroundMatch[1], 10)];
  }

  // Handle "X+" format - treat as single value
  const plusMatch = incomeStr.match(/^(\d+)\+$/);
  if (plusMatch) {
    return [parseInt(plusMatch[1], 10)];
  }

  // Handle range format "X-Y"
  const rangeMatch = incomeStr.match(/^(\d+)\s*[-–]\s*(\d+)$/);
  if (rangeMatch) {
    return [parseInt(rangeMatch[1], 10), parseInt(rangeMatch[2], 10)];
  }

  // Handle single number
  const singleMatch = incomeStr.match(/^\d+$/);
  if (singleMatch) {
    return [parseInt(incomeStr, 10)];
  }

  return null;
}

function calculateMinimumIncome(
  rent: number | null | undefined,
  minimumIncomeRequirement: RenterRequirementsMinimumIncome | null | undefined,
): number | null {
  if (!rent || !minimumIncomeRequirement) {
    return null;
  }

  switch (minimumIncomeRequirement) {
    case RenterRequirementsMinimumIncome.X1_RENT:
      return rent;
    case RenterRequirementsMinimumIncome.X1_5_RENT:
      return rent * 1.5;
    case RenterRequirementsMinimumIncome.X2_RENT:
      return rent * 2;
    case RenterRequirementsMinimumIncome.X2_5_RENT:
      return rent * 2.5;
    case RenterRequirementsMinimumIncome.X3_RENT:
      return rent * 3;
    case RenterRequirementsMinimumIncome.X3_5_RENT:
      return rent * 3.5;
    case RenterRequirementsMinimumIncome.NO_REQUIREMENT:
      return 0;
    default:
      return null;
  }
}
